// src/views/components/PropertyCard/PropertyCard.jsx
import React, { memo } from 'react';
import { MapPin, Maximize, Home, MessageCircle, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import Button from '../Button/Button';
import TiltCard from '../Motion/TiltCard';
import { useLeadContext } from '../../../controllers/context/LeadContext';
import { useAuth } from '../../../controllers/context/AuthContext';
import { LEAD_SOURCES, LEAD_TYPES } from '../../../models/leadTypes';
import { generateWhatsAppLink } from '../../../controllers/services/leadService';
import { useSiteSettings } from '../../../controllers/context/SiteSettingsContext';
import '../components.css';

const PropertyCard = memo(({ property }) => {
  const { openPopup } = useLeadContext();
  const { shortlistedProperties, toggleShortlist } = useAuth();
  const { whatsappPhone } = useSiteSettings();
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();

  const isSaved = shortlistedProperties.includes(property.id);

  const handleCardClick = () => {
    navigate(`/property/${property.id}`);
  };

  const handleWhatsApp = (e) => {
    e.stopPropagation();
    // Anti-scraping redirect: logs lead click and securely opens WhatsApp
    window.open(`/wa/${property.id}`, '_blank');
  };

  const handleGetDetails = (e) => {
    e.stopPropagation();
    openPopup(`Get details for ${property.name}`, LEAD_SOURCES.PROPERTY_ENQUIRY, LEAD_TYPES.BUYER);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, filter: 'blur(4px)' }}
      whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      style={{ height: '100%' }}
    >
      <TiltCard
        maxTilt={8}
        className="property-card"
        onClick={handleCardClick}
        style={{ cursor: 'pointer' }}
      >
        <div className="property-image-wrap" style={{ position: 'relative', overflow: 'hidden' }}>
          <motion.img
            src={property.image}
            alt={property.name}
            className="property-image"
            loading="lazy"
            decoding="async"
            whileHover={shouldReduceMotion ? {} : { scale: 1.08 }}
            transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
          />
          <div style={{ position: 'absolute', top: '1rem', left: '1rem', display: 'flex', flexDirection: 'column', gap: '6px', zIndex: 4, alignItems: 'flex-start' }}>
            <div className="property-status glass" style={{ position: 'static' }}>{property.status}</div>
            {property.direct_contact && (
              <div style={{
                background: 'linear-gradient(135deg, #A8080C, #85060A)',
                color: '#ffffff',
                padding: '3px 8px',
                borderRadius: '12px',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.3px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                boxShadow: '0 4px 10px rgba(168, 8, 12, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                textTransform: 'uppercase'
              }}>
                <span>Direct from Owner</span>
              </div>
            )}
          </div>

          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              toggleShortlist(property.id);
            }}
            whileTap={{ scale: 0.7 }}
            whileHover={{ scale: 1.2, rotate: 10 }}
            transition={{ type: 'spring', stiffness: 450, damping: 15 }}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(8px)',
              border: 'none',
              borderRadius: '50%',
              width: '42px',
              height: '42px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
              color: isSaved ? '#ef4444' : '#94a3b8',
              zIndex: 5,
            }}
          >
            <Heart size={20} fill={isSaved ? '#ef4444' : 'none'} />
          </motion.button>
        </div>

        <div className="property-content">
          <motion.div className="property-price" whileHover={{ x: 2 }}>
            {property.price}
          </motion.div>
          <h3 className="property-title">{property.name}</h3>
          <div className="property-location">
            <MapPin size={16} />
            {property.location}
          </div>

          <div className="property-features">
            <div className="feature-item">
              <Home size={16} className="text-accent" />
              {property.type}
            </div>
            <div className="feature-item">
              <Maximize size={16} className="text-accent" />
              {property.area}
            </div>
          </div>

          <div className="property-actions">
            <Button variant="whatsapp" icon={MessageCircle} onClick={handleWhatsApp}>
              WhatsApp
            </Button>
            <Button variant="primary" onClick={handleGetDetails}>
              Enquire
            </Button>
          </div>
        </div>
      </TiltCard>
    </motion.div>
  );
});

export default PropertyCard;
